import React, { useState } from "react";
import { Field, Form, Formik } from "formik";
import * as Yup from "yup";
import { usePicklist } from "../../contexts/PicklistProvider";
import ClayLoadingIndicator from "@clayui/loading-indicator";

const SindicateInfo = ({ data, updateData, onNextStep, onBackStep }) => {
	const { getPicklistByCode, externalReferenceCodes, loading } =
		usePicklist();

	const currentStatePicklistItems = loading
		? []
		: getPicklistByCode(externalReferenceCodes.CURRENTSTATE);

	const allowedSituationPicklistKeys = ["Ativo", "Aposentado", "Pensionista"];

	const filteredcurrentStatePicklistItems = currentStatePicklistItems.filter(
		(picklist) => allowedSituationPicklistKeys.includes(picklist.key)
	);

	const lotacaoPicklistItems = loading
		? []
		: getPicklistByCode(externalReferenceCodes.ORGAO);

	const minDate = new Date();
	const day = String(minDate.getDate()).padStart(2, "0");
	const month = String(minDate.getMonth() + 1).padStart(2, "0");
	const minDateFormatted = `${minDate.getFullYear()}-${month}-${day}`;

	const [isPensionista, setIsPensionista] = useState(
		data.estadoAtual === "Pensionista"
	);

	const [lotacaoSearch, setLotacaoSearch] = useState(() => {
		if (!data.lotacao) return "";
		const found = lotacaoPicklistItems.find((i) => i.key === data.lotacao);
		return found ? found.name : "";
	});
	const [lotacaoOpen, setLotacaoOpen] = useState(false);

	if (loading) {
		return <ClayLoadingIndicator />;
	}

	const validationSchema = Yup.object().shape({
		// estadoAtual: Yup.string().required("Situação atual é obrigatório"),
		matriculaPCDF: Yup.string().required("Matrícula PCDF é obrigatório"),
		matriculaSIAPE: Yup.string().required("Matrícula SIAPE é obrigatório"),
		cargo: Yup.string().required("Cargo é obrigatório"),
		// lotacao: Yup.string().required("Lotação é obrigatório"),
		dataAdmissaoPCDF: Yup.date().required("Data de admissão é obrigatório"),
	});

	return (
		<div>
			<h4>Dados Sindicais</h4>
			<Formik
				initialValues={{
					estadoAtual: data.estadoAtual || "",
					matriculaPCDF: data.matriculaPCDF || "",
					matriculaSIAPE: data.matriculaSIAPE || "",
					matriculaInstituidor: data.matriculaInstituidor || "",
					cargo: data.cargo || "",
					lotacao: data.lotacao || "",
					dataAdmissaoPCDF: data.dataAdmissaoPCDF || "",
				}}
				validationSchema={validationSchema}
				onSubmit={(values, actions) => {
					try {
						updateData(values);
						onNextStep();
						actions.setSubmitting(false);
					} catch (error) {
						console.error("Error during submission:", error);
						actions.setSubmitting(false);
						actions.setStatus("Erro ao enviar o formulário");
					}
				}}
			>
				{({ errors, isSubmitting, setFieldValue, submitCount, touched }) => (
					<Form>
						{submitCount > 0 && Object.keys(errors).length > 0 && (
							<div className="alert alert-danger mb-3">
								<span className="text-4">
									Verifique os campos abaixo para continuar:
								</span>
								<ul className="mb-0">
									{Object.entries(errors).map(
										([field, error]) => (
											<li key={field}>{error}</li>
										)
									)}
								</ul>
							</div>
						)}

						<div className="row">
							<div className="form-group required d-flex flex-column col-12 col-md-4">
								<label htmlFor="cargo" className="form-label">
									Cargo
								</label>
								<Field
									id="occupation"
									name="cargo"
									type="text"
									className="form-control form-field"
								/>
								{errors.cargo && touched.cargo ? (
									<div className="invalid-feedback d-block">
										{errors.cargo}
									</div>
								) : null}
							</div>

							{lotacaoPicklistItems.length > 0 && (
								<div className="form-group required col-md-8 col-12">
									<label
										htmlFor="lotacao"
										className="form-label"
									>
										Lotação
									</label>
									<div className="lotacao-searchable-select" style={{ position: "relative" }}>
										<input
											type="text"
											className="form-control form-field"
											placeholder="Buscar lotação..."
											value={lotacaoSearch}
											onChange={(e) => {
												setLotacaoSearch(e.target.value);
												setLotacaoOpen(true);
											}}
											onFocus={() => setLotacaoOpen(true)}
											onBlur={() => setTimeout(() => setLotacaoOpen(false), 150)}
										/>
										{lotacaoOpen && (
											<ul
												style={{
													position: "absolute",
													top: "100%",
													left: 0,
													right: 0,
													zIndex: 1000,
													background: "#fff",
													border: "1px solid #ced4da",
													borderRadius: "0.25rem",
													maxHeight: "200px",
													overflowY: "auto",
													listStyle: "none",
													margin: 0,
													padding: 0,
												}}
											>
												{lotacaoPicklistItems
													.filter((item) =>
														item.name
															.toLowerCase()
															.includes(lotacaoSearch.toLowerCase())
													)
													.map((item) => (
														<li
															key={item.key}
															onMouseDown={() => {
																setFieldValue("lotacao", item.key);
																setLotacaoSearch(item.name);
																setLotacaoOpen(false);
															}}
															style={{
																padding: "0.375rem 0.75rem",
																cursor: "pointer",
															}}
															onMouseEnter={(e) =>
																(e.currentTarget.style.background = "#f8f9fa")
															}
															onMouseLeave={(e) =>
																(e.currentTarget.style.background = "")
															}
														>
															{item.name}
														</li>
													))}
											</ul>
										)}
									</div>
									{errors.lotacao && touched.lotacao ? (
										<div className="invalid-feedback d-block">
											{errors.lotacao}
										</div>
									) : null}
								</div>
							)}
						</div>

						<div className="row">
							<div className="form-group required d-flex flex-column col-4">
								<label
									htmlFor="dataAdmissaoPCDF"
									className="form-label"
								>
									Data de Admissão na PCDF
								</label>
								<Field
									id="dataAdmissaoPCDF"
									name="dataAdmissaoPCDF"
									type="date"
									max={minDateFormatted}
									className="form-control form-field"
								/>
								{errors.dataAdmissaoPCDF &&
								touched.dataAdmissaoPCDF ? (
									<div className="invalid-feedback d-block">
										{errors.dataAdmissaoPCDF}
									</div>
								) : null}
							</div>
						</div>

						<div className="row">
							<div className="form-group required d-flex flex-column col-12 col-md-6">
								<label
									htmlFor="matriculaPCDF"
									className="form-label"
								>
									Matrícula PCDF
								</label>
								<Field
									id="matriculaPCDF"
									name="matriculaPCDF"
									type="number"
									className="form-control form-field"
									placeholder="00686797"
								/>
								{errors.matriculaPCDF &&
								touched.matriculaPCDF ? (
									<div className="invalid-feedback d-block">
										{errors.matriculaPCDF}
									</div>
								) : null}
							</div>

							<div className="form-group required d-flex flex-column col-12 col-md-6">
								<label
									htmlFor="matriculaSIAPE"
									className="form-label"
								>
									Matrícula SIAPE
								</label>
								<Field
									id="matriculaSIAPE"
									name="matriculaSIAPE"
									type="text"
									className="form-control form-field"
								/>
								{errors.matriculaSIAPE &&
								touched.matriculaSIAPE ? (
									<div className="invalid-feedback d-block">
										{errors.matriculaSIAPE}
									</div>
								) : null}
							</div>
						</div>

						<div className="row">
							{filteredcurrentStatePicklistItems.length > 0 && (
								<div className="form-group required col-md-6 col-12">
									<label
										htmlFor="estadoAtual"
										className="form-label"
									>
										Situação atual
									</label>
									<Field
										as="select"
										id="estadoAtual"
										name="estadoAtual"
										className="form-control"
										disabled={loading}
										onChange={(e) => {
											const value = e.target.value;
											setFieldValue("estadoAtual", value);
											setIsPensionista(
												value === "Pensionista"
											);
										}}
									>
										<option value="">Selecione</option>
										{filteredcurrentStatePicklistItems.map(
											(item) => (
												<option
													key={item.key}
													value={item.key}
												>
													{item.name}
												</option>
											)
										)}
									</Field>
									{errors.estadoAtual &&
									touched.estadoAtual ? (
										<div className="invalid-feedback d-block">
											{errors.estadoAtual}
										</div>
									) : null}
								</div>
							)}

							<div className="form-group d-flex flex-column col-12 col-md-6">
								<label
									htmlFor="matriculaInstituidor"
									className="form-label"
								>
									Matrícula Instituidor
								</label>
								<Field
									id="matriculaInstituidor"
									name="matriculaInstituidor"
									type="text"
									className="form-control form-field"
									disabled={!isPensionista}
								/>
								{errors.matriculaInstituidor &&
								touched.matriculaInstituidor ? (
									<div className="invalid-feedback d-block">
										{errors.matriculaInstituidor}
									</div>
								) : null}
							</div>
						</div>

						<div className="d-flex justify-content-between">
							<button
								type="button"
								className="btn btn-secondary"
								onClick={onBackStep}
							>
								Anterior
							</button>
							<button
								type="submit"
								className="btn btn-primary"
								disabled={isSubmitting}
							>
								Próximo
							</button>
						</div>
					</Form>
				)}
			</Formik>
		</div>
	);
};

export default SindicateInfo;
